import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useRef, useState } from 'preact/hooks';
import { VirtualList } from '../utils/index.js';
import { getApplicableActions } from './Item/components/ItemActionsMenu.js';
import ItemActionsPopover from './Item/components/ItemActionsPopover.js';
import useItemMenu from './Item/components/useItemMenu.js';
import Item from './Item/index.js';
function Browser(props) {
    const { displayedPartialTree, viewType, toggleCheckbox, handleScroll, showTitles, i18n, isLoading, onCancelLoading, openFolder, noResultsLabel, virtualList, utmSource, actions = [], runAction, selectable = true, onFileClick, } = props;
    const [isShiftKeyPressed, setIsShiftKeyPressed] = useState(false);
    // At most one item actions menu is open; it lives here (not in the item) so
    // it can be rendered outside the scrolling list.
    const menu = useItemMenu(displayedPartialTree);
    const bodyRef = useRef(null);
    // This records whether the user is holding the SHIFT key this very moment.
    // Typically, this is implemented using `onClick((e) => e.shiftKey)` -
    // however we can't use that, because for accessibility reasons
    // we're using html tags that don't support `e.shiftKey` property (see #3768).
    useEffect(() => {
        const handleKeyUp = (e) => {
            if (e.key === 'Shift')
                setIsShiftKeyPressed(false);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Shift')
                setIsShiftKeyPressed(true);
        };
        document.addEventListener('keyup', handleKeyUp);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    // A long operation replaced the list, and with it whatever had focus: its
    // Stop button takes over.
    const stopRef = useRef(null);
    const canStop = Boolean(isLoading && onCancelLoading);
    useEffect(() => {
        if (canStop)
            stopRef.current?.focus();
    }, [canStop]);
    if (isLoading) {
        return (_jsxs("div", { className: "uppy-Provider-loading", children: [_jsx("span", { role: "status", children: typeof isLoading === 'string' ? isLoading : i18n('loading') }), onCancelLoading && (_jsx("button", { ref: stopRef, type: "button", className: "uppy-u-reset uppy-c-btn uppy-c-btn-link uppy-Provider-loadingCancel", onClick: onCancelLoading, children: i18n('stopOperation') }))] }));
    }
    if (displayedPartialTree.length === 0) {
        return _jsx("div", { className: "uppy-Provider-empty", children: noResultsLabel });
    }
    const renderItem = (item) => (_jsx(Item, { viewType: viewType, toggleCheckbox: (event) => {
            event.stopPropagation();
            event.preventDefault();
            // Prevent shift-clicking from highlighting file names
            // (https://stackoverflow.com/a/1527797/3192470)
            document.getSelection()?.removeAllRanges();
            toggleCheckbox(item, isShiftKeyPressed);
        }, showTitles: showTitles, i18n: i18n, openFolder: openFolder, file: item, utmSource: utmSource, actions: actions, menuOpen: menu.open?.item.id === item.id, toggleMenu: runAction ? (anchor) => menu.toggle(item.id, anchor) : undefined, selectable: selectable, onFileClick: onFileClick }, item.id));
    const popover = menu.open && runAction ? (_jsx(ItemActionsPopover, { file: menu.open.item, actions: getApplicableActions(actions, menu.open.item), anchor: menu.open.anchor, containerRef: bodyRef, runAction: runAction, onClose: menu.close, i18n: i18n }, menu.open.item.id)) : null;
    // todo remove virtuallist option and always use virtual list
    if (virtualList) {
        return (_jsxs("div", { className: "uppy-ProviderBrowser-body", ref: bodyRef, children: [_jsx(VirtualList, { className: "uppy-ProviderBrowser-list", data: displayedPartialTree, renderRow: renderItem, rowHeight: 35.5 }), popover] }));
    }
    return (_jsxs("div", { className: "uppy-ProviderBrowser-body", ref: bodyRef, children: [_jsx("ul", { className: "uppy-ProviderBrowser-list", onScroll: handleScroll, 
                // making <ul> not focusable for firefox
                tabIndex: -1, children: displayedPartialTree.map(renderItem) }), popover] }));
}
export default Browser;
